import * as THREE from 'three';
import { clearLine, type Assembly, type Bounds } from '@vrlearning/lesson-schema';
import type { Stage } from './stage.js';

/** How far out the clear line is drawn, and how far round. */
const LINE_DISTANCE = 3;
const LINE_REACH = 70;

/**
 * `?frame=1` in the desktop preview: the box framing measured the step's
 * subject by, and the clear line its lowest point must stay above -- so
 * whoever writes a lesson can see why the pupil stands where they do, and why
 * the lectern moved. Never in a headset: the preview only.
 */
export class FrameAids {
  readonly #box = new THREE.Box3Helper(new THREE.Box3(), 0xff3fd2);
  readonly #line: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>;

  constructor(readonly stage: Stage) {
    // An arc at one horizontal distance is at one elevation all the way round.
    const points: THREE.Vector3[] = [];
    for (let az = -LINE_REACH; az <= LINE_REACH; az += 5) {
      const a = (az * Math.PI) / 180;
      points.push(new THREE.Vector3(Math.sin(a) * LINE_DISTANCE, 0, -Math.cos(a) * LINE_DISTANCE));
    }
    this.#line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(points),
      new THREE.LineBasicMaterial({ color: 0xff3fd2, depthTest: false }),
    );
    this.#line.renderOrder = 997;
    this.#box.visible = false;
    stage.scene.add(this.#box);
    stage.rig.add(this.#line);
  }

  show(subject: Bounds | undefined, eye: number, assembly?: Assembly): void {
    this.#box.visible = subject !== undefined;
    if (subject) {
      this.#box.box.min.set(...subject.box.min);
      this.#box.box.max.set(...subject.box.max);
    }
    this.#line.position.y =
      eye + Math.tan((clearLine(eye, assembly) * Math.PI) / 180) * LINE_DISTANCE;
  }

  dispose(): void {
    this.#box.removeFromParent();
    this.#box.geometry.dispose();
    (this.#box.material as THREE.Material).dispose();
    this.#line.removeFromParent();
    this.#line.geometry.dispose();
    this.#line.material.dispose();
  }
}
